import React, { useState } from "react"
import EventResourcesView from "components/layout/EventResourcesView"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import * as api from "api/forms"
import * as Yup from "yup"
import { refetchForms, updateForm } from "context/actions"
import { useQueryClient } from "react-query"
import {
  DioboxEvent, Form, FormFormType, FormFormValues, FormType,
  GuestStrategy,
} from "sharedTypes"
import useCurrentAccount from "services/useCurrentAccount"
import { Boolean } from "utilities/enums"
import { useHistory } from "react-router"
import { eventFormPath } from "utilities/routes"
import { stripOutQuestionsFormBlockData } from "components/Builder/helpers"
import { formIconAndType, getEnabledFormTypesBySubscriptionLevel, isGuestStrategyEnabled } from "utilities/forms"
import useGenerateName from "services/useGenerateName"
import useFeatureFlag from "services/featureFlags"
import { stripHtmlTags } from "utilities/strings"
import Link from "components/elements/Link"
import EventCapacity from "modals/EventCapacity"
import Registration from "./Registration"
import Ticket from "./Ticket/Ticket"
import RSVP from "./RSVP"
import Waitlist from "./Waitlist"
import ListItem from "./ListItem"
import Survey from "./Survey"

type Props = {
  event: DioboxEvent
  onClose: () => void
  onSuccess?: (formData: Form) => void
  form?: Form
}

export const formFormInitialValues = {
  type: FormType.Registration,
  disabled: false,
  private: false,
  limitPerSubmission: 10,
  guestStrategy: GuestStrategy.Create,
  confirmationEmailsDisabled: Boolean.False,
}

const FormForm = ({
  event,
  onClose,
  onSuccess,
  form: currentForm,
}: Props) => {
  const {
    id: eventId, currency, sendConfirmationEmails, capacity,
  } = event
  const [capacityWindow, setCapacityWindow] = useState<{
    languageTemplateId: string | null
  } | null>(null)
  const queryClient = useQueryClient()
  const history = useHistory()
  const { subscription } = useCurrentAccount()
  const { generateFormName } = useGenerateName(eventId)
  const { surveyForms: surveyFormsEnabled, eventCapacity: eventCapacityEnabled } = useFeatureFlag()

  const handleSuccess = (formData: Form) => {
    onSuccess?.(formData)
    refetchForms(queryClient, eventId)
    onClose()
  }

  const handleSubmit = (values: Form & Pick<FormFormValues, "languageTemplateId">) => {
    // The Language field's blank option ("Use Event Default") submits as "" — normalize to
    // null so the form has no explicit override and keeps following the event's default
    // going forward, rather than getting pinned to whatever that default happened to be now.
    const normalizedValues = { ...values, languageTemplateId: values.languageTemplateId || null }

    if (currentForm) {
      const formWithoutQuestionsFormBlockData = stripOutQuestionsFormBlockData(normalizedValues)
      const submissionMessageIsEmpty = values.submissionMessage === null || stripHtmlTags(values.submissionMessage) === ""

      return api.patchForm(eventId,
        currentForm.externalId,
        {
          ...formWithoutQuestionsFormBlockData,
          submissionMessage: submissionMessageIsEmpty ? "" : values.submissionMessage,
        })
        .then(({ data: updatedForm }) => {
          handleSuccess(updatedForm)
          updateForm(queryClient, currentForm.externalId, updatedForm)
        })
    }

    return api.postForm(eventId, normalizedValues).then(({ data: { externalId }, data }) => {
      handleSuccess(data)
      history.push(eventFormPath(eventId, externalId))
    })
  }

  const enabledFormTypes = getEnabledFormTypesBySubscriptionLevel(subscription)

  const validationSchema = Yup.object().shape({
    disabled: Yup.string().oneOf([Boolean.True, Boolean.False]),
    name: Yup.string().required(),
    description: Yup.string().nullable(),
    private: Yup.string().oneOf([Boolean.True, Boolean.False]),
    submissionsEndAt: Yup.date().nullable(),
    type: Yup.string().oneOf(enabledFormTypes),
    quantity: Yup.number()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .when("type", {
        is: (type) => [FormType.Survey].includes(type),
        then: (schema) => schema.nullable(),
        otherwise: (schema) => schema.min(1).nullable(),
      }),
    limitPerSubmission: Yup.number().min(1).max(10).nullable(),
    price: Yup.string().when(["variablePrice", "type"], {
      is: (variablePrice, type) => variablePrice !== Boolean.True && type === FormType.Ticket,
      then: (schema) => schema.notOneOf(["0.00"]).required(),
      otherwise: (schema) => schema.nullable(),
    }),
    variablePrice: Yup.string().oneOf([Boolean.True, Boolean.False]),
    submissionMessage: Yup.string().nullable(),
    guestStrategy: Yup.string().when("type", {
      is: (type) => isGuestStrategyEnabled(type),
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable(),
    }),
    confirmationEmailsDisabled: Yup.string().oneOf([Boolean.True, Boolean.False]),
  })

  let initialValues: FormFormValues = {
    ...formFormInitialValues,
    name: generateFormName(FormType.Registration),
    languageTemplateId: "",
  }

  if (currentForm) {
    initialValues = {
      ...currentForm,
      confirmationEmailsDisabled:
        currentForm.confirmationEmailsDisabled ? Boolean.True : Boolean.False,
      languageTemplateId: currentForm.languageTemplateId ?? "",
    }
  }

  return (
    <>
      <ModalForm
        title={`${currentForm ? "Edit" : "New"} Form`}
        size="5xl"
        h="80%"
        noPadding
        onClose={onClose}
        onSubmit={handleSubmit}
        submitText={currentForm ? "Save" : "Create"}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          control, watch, reset,
        }) => {
          const values: FormFormValues = watch()
          const quantityHelperText = eventCapacityEnabled && capacity ? (
            <>
              Closes when {values.quantity ? `${values.quantity} are registered or ` : ""}
              the event reaches capacity ({capacity.toLocaleString()}).{" "}
              <Link
                onClick={() => setCapacityWindow({
                  languageTemplateId: values.languageTemplateId || null,
                })}
              >
                Edit
              </Link>
            </>
          ) : undefined

          const registationIconAndType = formIconAndType(FormType.Registration)
          const ticketIconAndType = formIconAndType(FormType.Ticket)
          const rsvpIconAndType = formIconAndType(FormType.RSVP)
          const waitlistIconAndType = formIconAndType(FormType.Waitlist)
          const surveyIconAndType = formIconAndType(FormType.Survey)

          const formTypes: FormFormType[] = [
            {
              name: registationIconAndType.type,
              icon: registationIconAndType.icon,
              type: FormType.Registration,
              component: <Registration
                control={control}
                quantityHelperText={quantityHelperText}
                eventId={eventId}
                name={registationIconAndType.type}
                sendConfirmationEmails={sendConfirmationEmails}
              />,
            },
            {
              name: ticketIconAndType.type,
              icon: ticketIconAndType.icon,
              type: FormType.Ticket,
              component: <Ticket
                control={control}
                quantityHelperText={quantityHelperText}
                eventId={eventId}
                name={ticketIconAndType.type}
                currency={currency}
                sendConfirmationEmails={sendConfirmationEmails}
              />,
            },
            {
              name: rsvpIconAndType.type,
              icon: rsvpIconAndType.icon,
              type: FormType.RSVP,
              component: <RSVP
                control={control}
                quantityHelperText={quantityHelperText}
                eventId={eventId}
                showUpgradeAlert={!enabledFormTypes.includes(FormType.RSVP)}
                name={rsvpIconAndType.type}
                sendConfirmationEmails={sendConfirmationEmails}
              />,
            },
            {
              name: waitlistIconAndType.type,
              icon: waitlistIconAndType.icon,
              type: FormType.Waitlist,
              component: <Waitlist
                control={control}
                eventId={eventId}
                showUpgradeAlert={!enabledFormTypes.includes(FormType.Waitlist)}
                name={waitlistIconAndType.type}
                sendConfirmationEmails={sendConfirmationEmails}
              />,
            },

          ]

          if (surveyFormsEnabled) {
            formTypes.push({
              name: surveyIconAndType.type,
              icon: surveyIconAndType.icon,
              type: FormType.Survey,
              component: <Survey
                control={control}
                eventId={eventId}
                showUpgradeAlert={!enabledFormTypes.includes(FormType.Waitlist)}
                name={surveyIconAndType.type}
                sendConfirmationEmails={sendConfirmationEmails}
              />,
            })
          }

          const selectedType = formTypes.find(
            (formType) => formType.type === values.type,
          )

          return (
            <EventResourcesView
              listWidth="xs"
              showBorder={false}
              contentMaxWidth="full"
              contentPadding={6}
              listItems={formTypes.map(
                (f) => (
                  <ListItem
                    key={f.type}
                    form={f}
                    initialValues={initialValues}
                    values={values}
                    reset={reset}
                    disabled={currentForm && selectedType?.type !== f.type}
                    eventId={eventId}
                  />
                ),
              )}
              selectedItem={selectedType?.component}

            />
          )
        }}
      </ModalForm>
      {/* Outside the form above: a submit inside it would bubble to that form too */}
      {capacityWindow && (
        <EventCapacity
          event={event}
          languageTemplateId={capacityWindow.languageTemplateId}
          onHide={() => setCapacityWindow(null)}
        />
      )}
    </>
  )
}

export default FormForm
